import { combineLatest } from "rxjs";
import { hasOwnProperty } from "@microsoft/applicationinsights-core-js";
import { DestroyRef, Injectable, computed, inject, signal } from "@angular/core";

import { INavNode } from "@logex/framework/lg-application";

import { AppStateService } from "./app-state.service";
import { Router } from "@angular/router";
import { AppDefinitionManagerService } from "./app-definition-manager.service";
import { FormConfigurationElementType, SubjectData } from "@fhir-api";
import {
    apiPathToName,
    pathDatasetName,
    pathToSubjectUrl,
    rootDatasetFromPath,
    rootSubjectFromApi
} from "@app/util/data-path-util";
import { RootData, SubjectInstance } from "./app-subject-manager.service";
import { AppProjectsService } from "../app-projects.service";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";

export interface DataPath {
    subjectUri: string;
    datasetUri: string;
    section?: string;
    category?: string;
    variable?: string;
}

export interface SectionPath extends DataPath {
    section: string;
}

export interface CategoryPath extends SectionPath {
    category: string;
}

export interface VariablePath extends DataPath {
    variable: string;
}

export interface CurrentLocation {
    openedDialog: CategoryPath | null;
    rootSubject: DataPath | null;
}

export type NavigationTree = Record<string, NavigationTreeSubject>;

export interface NavigationTreeSubject {
    subjectUri: string;
    datasetUri: string;
    sections: NavigationTreeSection[];
}

export interface NavigationTreeSection {
    name: string;
    categories: NavigationTreeCategory[];
    datasetUris?: string[];
}

export interface NavigationTreeCategory {
    name: string;
    elements: NavigationTreeElement[];
}

export interface NavigationTreeElement {
    name: string;
    type: FormConfigurationElementType;
}

@Injectable({
    providedIn: "root"
})
export class AppNavigationManagerService {
    private _appDefinitionManager = inject(AppDefinitionManagerService);
    private _appStateService = inject(AppStateService);
    private _router = inject(Router);
    private _projectsService = inject(AppProjectsService);
    private _destroyRef = inject(DestroyRef);

    private _currentLocation = signal<CurrentLocation>({
        openedDialog: null,
        rootSubject: null
    });
    currentLocation = this._currentLocation.asReadonly();
    currentLocation$ = toObservable(this._currentLocation);

    rootDataset$ = this._projectsService.rootDataset$;

    private _navigationTree = signal<NavigationTree>({});
    navigationTree = this._navigationTree.asReadonly();

    currentSection = computed(() => this.currentLocation()?.rootSubject?.section);
    currentPath = computed(() => this.currentLocation()?.rootSubject);
    openedDialog = computed(() => this.currentLocation()?.openedDialog);

    init(): void {
        this._update(null);
        this.rootDataset$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(rootDataset => {
            this._update(rootDataset);
        });
    }

    triggerNavigationTreeUpdate(subject?: SubjectInstance | null): void {
        const rootDataset = this._projectsService.rootDataset();
        this._update(rootDataset);
    }

    closeInitSubject(): void {
        this._currentLocation.update(location => ({
            ...location,
            openedDialog: null
        }));
    }

    openInitSubject(path: CategoryPath): void {
        this._currentLocation.update(location => ({
            ...location,
            openedDialog: { ...path, section: "", category: "" }
        }));
    }

    recalculateNavigationTree(rootDataset: RootData): void {
        const navigationTree = this._generateNavigationTree(rootDataset);
        this._navigationTree.set(navigationTree);
    }

    private _generateNavigationTree(subjects: Record<string, SubjectInstance>): NavigationTree {
        return Object.entries(subjects).reduce((acc, [subjectUri, subject]) => {
            acc[subjectUri] = this._generateSubjectNavigationTree(subject);
            return acc;
        }, {} as NavigationTree);
    }

    private _generateSubjectNavigationTree(subject: SubjectInstance): NavigationTreeSubject {
        const datasetDefinition = this._appDefinitionManager.getDatasetDefinition(
            apiPathToName(subject.datasetUri)
        );
        const sections = datasetDefinition.formConfiguration.sections;
        return {
            subjectUri: subject.subjectUri,
            datasetUri: subject.datasetUri,
            sections: sections.map(section => ({
                name: section.name,
                categories: section.categories.map(category => ({
                    name: category.name,
                    elements: category.elements.map(element => ({
                        name: element.name,
                        type: element.type
                    }))
                })),
                datasetUris: section.datasets
                    ? section.datasets.map(dataset => subject.datasetUri + "." + dataset)
                    : undefined
            }))
        };
    }

    navigateTo(path: DataPath, subject?: SubjectInstance | null): void {
        const project = this._appStateService.selectedRegistryName();
        const organization = this._appStateService.selectedOrganizationUri();
        if (!project || !organization) {
            throw new Error("Project or organization is not set");
        }
        let navigationUrl = "/registry/" + organization + "/" + project + pathToSubjectUrl(path);
        const node = this.findNavigationTreeSubject(path.subjectUri);
        if (!node) {
            throw new Error("Invalid navigation path");
        }
        const section =
            node.sections.find(section => section.name == path.section) ?? node.sections[0];
        if (!section) {
            throw new Error("subject has no sections");
        }
        path.section = section.name;
        navigationUrl += "?section=" + section.name;

        if (path.variable) {
            navigationUrl += "&variable=" + path.variable;
        }

        this._currentLocation.update(location => ({
            ...location,
            rootSubject: path
        }));

        this._router.navigateByUrl(navigationUrl, {
            replaceUrl: true
        });
        this._update(rootDatasetFromPath(path));
    }

    findNavigationTreeSubject(subjectUri: string): NavigationTreeSubject | null {
        return this.navigationTree()[subjectUri] ?? null;
    }

    findNavigationTreeSection(
        subjectUri: string,
        sectionName: string
    ): NavigationTreeSection | null {
        return (
            this.navigationTree()[subjectUri].sections.find(
                section => section.name == sectionName
            ) ?? null
        );
    }

    matchPaths(searchPath: DataPath, matchPath: DataPath, full: boolean = false): boolean {
        if (
            !searchPath.subjectUri.startsWith(matchPath.subjectUri) ||
            (full && searchPath.subjectUri !== matchPath.subjectUri)
        ) {
            return false;
        }
        if (
            matchPath.section &&
            matchPath.section !== "" &&
            full &&
            searchPath.section !== matchPath.section
        ) {
            return false;
        }
        if (
            matchPath.category &&
            matchPath.category !== "" &&
            full &&
            searchPath.category !== matchPath.category
        ) {
            return false;
        }
        if (
            matchPath.variable &&
            matchPath.variable !== "" &&
            full &&
            searchPath.variable !== matchPath.variable
        ) {
            return false;
        }
        return true;
    }

    private _update(rootDataset: string | null): void {
        const appNavigation = this._getAppNavigation(rootDataset);
        this._appStateService.setNavigation(appNavigation);
    }

    private _getAppNavigation(rootDataset?: string | null): INavNode[] {
        return [this._getRootNavigation(rootDataset)];
    }

    private _getProjectsNavigation(rootDataset?: string | null): INavNode[] {
        const nodes = [this._getSelectProjectNavNode()];
        if (rootDataset) {
            nodes.push(this._getSelectedProjectNavNode());
        }
        return nodes;
    }

    private _getRootNavigation(rootDataset?: string | null): INavNode {
        return {
            id: "root2",
            path: "",
            lid: "APP._Navigation.ToolName",
            data: {
                noPopup: true
            },
            children: this._getProjectsNavigation(rootDataset),
            onClickFn: () => {}
        };
    }

    private _getSelectProjectNavNode(): INavNode {
        return {
            id: "select",
            path: "select",
            data: {
                noPopup: true
            },
            lid: "APP._Navigation.Select_registry"
        };
    }

    private _getSelectedProjectNavNode(): INavNode {
        const project = this._appStateService.selectedRegistryName();
        const organization = this._appStateService.selectedOrganization();
        if (!project || !organization) {
            throw new Error("Project or organization is not set");
        }

        const projects =
            this._projectsService.getOrganizationProjects(organization.organizationId) ?? [];
        const listProjectNav: INavNode = {
            id: "list",
            path: "registry/" + organization.mrdmUri,
            name: project.toUpperCase(),
            noBreadcrumb: true,
            data: {
                noPopup: true
            },
            children: [
                ...projects.map(projectDef => {
                    const node: INavNode = {
                        id: "select-" + projectDef.uri,
                        path: projectDef.uri,
                        data: {
                            noPopup: true
                        },
                        lid: projectDef.label.toUpperCase()
                    };
                    return node;
                })
            ]
        };
        return listProjectNav;
    }
}
